/**
 * Chatbot Suggestions Service
 * Fetches suggested prompts for empty chat sessions from Drupal CMS.
 *
 * Sources (merged, de-duplicated):
 * 1. /api/chatbot-suggestion-question/list — dedicated chatbot suggestion content
 * 2. /api/Text-and-Sub-text-fields — optional categorized prompts (non-homepage keys)
 *
 * @module chatbot-suggestions.service
 */

import { proxyService } from './proxy.service';
import { axiosInstance } from '@services/api/axios.instance';

/** Homepage section keys — not chat suggestion prompts */
const HOMEPAGE_SECTION_KEYS = new Set(['banner', 'tool', 'course', 'news', 'kpi']);

export interface DrupalSuggestionQuestion {
  suggestion_question: string;
  field_tags?: string;
}

export interface DrupalTextFieldItem {
  field_key: string;
  field_text: string;
  field_sub_text: string;
}

export interface SuggestionQuestion {
  query: string;
  category: string;
}

const decodeHtmlEntities = (text: string): string => {
  if (!text) return '';
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
};

const normalizeCategory = (category?: string): string =>
  decodeHtmlEntities(category?.trim() || 'Explore');

const transformSuggestionQuestion = (
  drupalQuestion: DrupalSuggestionQuestion
): SuggestionQuestion => ({
  query: decodeHtmlEntities(drupalQuestion.suggestion_question?.trim() || ''),
  category: normalizeCategory(drupalQuestion.field_tags),
});

const isChatSuggestionField = (field: DrupalTextFieldItem): boolean => {
  const key = (field.field_key || '').trim().toLowerCase();
  if (!key || HOMEPAGE_SECTION_KEYS.has(key)) {
    return false;
  }
  return Boolean(field.field_text?.trim());
};

const transformSuggestionField = (field: DrupalTextFieldItem): SuggestionQuestion => ({
  query: decodeHtmlEntities(field.field_text.trim()),
  category: normalizeCategory(field.field_sub_text),
});

const mergeSuggestions = (...lists: SuggestionQuestion[][]): SuggestionQuestion[] => {
  const seen = new Set<string>();
  const merged: SuggestionQuestion[] = [];

  for (const list of lists) {
    for (const item of list) {
      const query = item.query.trim();
      const key = query.toLowerCase();
      if (!key || seen.has(key)) {
        continue;
      }
      seen.add(key);
      merged.push({ query, category: item.category });
    }
  }

  return merged;
};

class ChatbotSuggestionsService {
  private async fetchFromSuggestionQuestionList(): Promise<SuggestionQuestion[]> {
    const url = '/api/chatbot-suggestion-question/list?_format=json';
    const response = await proxyService.get<DrupalSuggestionQuestion[]>(url);

    if (!response.data || !Array.isArray(response.data)) {
      console.warn(
        '[ChatbotSuggestionsService] Invalid chatbot-suggestion-question response:',
        response.data
      );
      return [];
    }

    return response.data
      .map(transformSuggestionQuestion)
      .filter((item) => item.query.length > 0);
  }

  private async fetchFromTextFields(): Promise<SuggestionQuestion[]> {
    const url = '/api/Text-and-Sub-text-fields?_format=json';
    const response = await proxyService.get<DrupalTextFieldItem[]>(url);

    if (!response.data || !Array.isArray(response.data)) {
      return [];
    }

    return response.data
      .filter(isChatSuggestionField)
      .map(transformSuggestionField)
      .filter((item) => item.query.length > 0);
  }

  private async fetchFromChatApi(): Promise<SuggestionQuestion[]> {
    const response = await axiosInstance.get<{
      questions?: Array<{ question?: string; category?: string | null }>;
    }>('/api/v1/chat/suggested-questions');

    const questions = response.data?.questions;
    if (!Array.isArray(questions)) {
      return [];
    }

    return questions
      .map((item) => ({
        query: decodeHtmlEntities(item.question?.trim() || ''),
        category: normalizeCategory(item.category ?? undefined),
      }))
      .filter((item) => item.query.length > 0);
  }

  /**
   * Get suggested prompts for empty chat sessions.
   */
  async getSuggestedQuestions(): Promise<SuggestionQuestion[]> {
    const [dedicatedResult, textFieldsResult] = await Promise.allSettled([
      this.fetchFromSuggestionQuestionList(),
      this.fetchFromTextFields(),
    ]);

    const dedicated =
      dedicatedResult.status === 'fulfilled' ? dedicatedResult.value : [];
    const textFields =
      textFieldsResult.status === 'fulfilled' ? textFieldsResult.value : [];

    if (dedicatedResult.status === 'rejected') {
      console.error(
        '[ChatbotSuggestionsService] chatbot-suggestion-question fetch failed:',
        dedicatedResult.reason
      );
    }
    if (textFieldsResult.status === 'rejected' && import.meta.env.DEV) {
      console.warn(
        '[ChatbotSuggestionsService] Text-and-Sub-text-fields fetch failed:',
        textFieldsResult.reason
      );
    }

    let suggestions = mergeSuggestions(dedicated, textFields);

    if (suggestions.length === 0) {
      try {
        suggestions = await this.fetchFromChatApi();
      } catch (error) {
        console.error('[ChatbotSuggestionsService] chat API fallback failed:', error);
      }
    }

    if (import.meta.env.DEV) {
      console.debug('[ChatbotSuggestionsService] Fetched suggestions:', suggestions.length);
    }

    return suggestions;
  }
}

export const chatbotSuggestionsService = new ChatbotSuggestionsService();
export default chatbotSuggestionsService;
